import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const { user, token } = useAuth();

  useEffect(() => {
    if (user && token) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0 });
    }
  }, [user, token]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity, size, color) => {
    if (!user) {
      toast.error("Please login to add items to cart");
      return false;
    }
    try {
      await axios.post(`${API}/cart`, {
        product_id: productId,
        quantity,
        size,
        color,
      });
      await fetchCart();
      toast.success("Added to cart!");
      return true;
    } catch (error) {
      toast.error("Failed to add to cart");
      return false;
    }
  };

  const updateQuantity = async (productId, quantity, size, color) => {
    try {
      await axios.put(
        `${API}/cart/${productId}?quantity=${quantity}&size=${size}&color=${color}`
      );
      await fetchCart();
    } catch (error) {
      toast.error("Failed to update cart");
    }
  };

  const removeFromCart = async (productId, size, color) => {
    try {
      await axios.delete(
        `${API}/cart/${productId}?size=${size}&color=${color}`
      );
      await fetchCart();
      toast.success("Removed from cart");
    } catch (error) {
      toast.error("Failed to remove from cart");
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart`);
      setCart({ items: [], total: 0 });
    } catch (error) {
      console.error("Failed to clear cart:", error);
    }
  };

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        fetchCart,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
